import { Link, Cloud, ShieldCheck, Zap, Youtube } from 'lucide-react';
import wCloudWhite from '@/assets/roadmap/wcloudwhite.webp';
import youtubeLogo from '@/assets/logos/youtube-logo.webp';
import vimeoLogo from '@/assets/logos/vimeo-logo.webp';

export const LandingStorage = () => {
    return (
        <section className="py-24 lg:py-32 bg-gray-900 text-white overflow-hidden relative">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-1/3 h-full bg-blue-500/5 blur-[120px] pointer-events-none" />

            <div className="max-w-[1440px] mx-auto px-6 relative z-10">

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

                    {/* Header & External Links */}
                    <div className="animate-fade-in-left">
                        <div className="inline-block bg-primary/10 text-primary px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-[0.2em] mb-6">
                            Infraestrutura Híbrida
                        </div>

                        <h2 className="text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-8">
                            Sua Nuvem ou a Nossa.<br />
                            <span className="text-gray-400">Você tem o controle.</span>
                        </h2>

                        <p className="text-lg text-gray-400 mb-12 leading-relaxed font-medium">
                            Na Watch, acreditamos que a tecnologia deve se adaptar ao seu negócio, e não o contrário.
                            Por isso criamos um ecossistema de armazenamento flexível e poderoso.
                        </p>

                        {/* Feature Block: External Links */}
                        <div className="bg-gray-800/50 p-8 rounded-3xl border border-gray-700 hover:border-primary/50 transition-colors mb-8">
                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Link className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black mb-3 text-white">Liberdade de Conectividade</h3>
                                    <p className="text-gray-400 leading-relaxed text-sm lg:text-base">
                                        Já possui sua infraestrutura? Perfeito. Nossa plataforma aceita
                                        <span className="text-white font-bold"> links externos diretos</span> de seus servidores pessoais,
                                        AWS S3, Google Cloud ou qualquer CDN pública. Sem bloqueios, sem taxas escondidas.
                                        Você mantém o controle total dos seus arquivos de mídia.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Feature Block: Streaming */}
                        <div className="bg-gray-800/50 p-8 rounded-3xl border border-gray-700 hover:border-primary/50 transition-colors">
                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 bg-gray-700/50 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Youtube className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black mb-3 text-white">Streaming Integrado</h3>
                                    <p className="text-gray-400 leading-relaxed text-sm lg:text-base mb-4">
                                        Expanda suas possibilidades. Nosso sistema possui compatibilidade com links do
                                        <span className="text-white font-bold"> YouTube</span> e
                                        <span className="text-white font-bold"> Vimeo</span> para vídeos.
                                    </p>
                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center justify-center transition-all duration-300 group hover:scale-105">
                                            <img src={youtubeLogo} alt="YouTube" className="h-8 w-auto object-contain" />
                                        </div>
                                        <div className="flex items-center justify-center transition-all duration-300 group hover:scale-105 -mt-1">
                                            <img src={vimeoLogo} alt="Vimeo" className="h-8 w-auto object-contain" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Feature Block: Watch Cloud */}
                    <div className="relative animate-fade-in-right">
                        {/* Visual Effect Board */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-[40px] blur-xl transform rotate-3 scale-95 opacity-50" />

                        <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 lg:p-12 rounded-[40px] border border-gray-700/50 shadow-2xl relative">

                            <div className="mb-8">
                                <img src={wCloudWhite} alt="Watch Cloud Logo" className="h-16 w-auto object-contain" />
                            </div>

                            <h3 className="text-xl font-black mb-3 text-white">Armazenamento em nuvem</h3>
                            <p className="text-lg text-gray-400 mb-10 leading-relaxed">
                                Para quem exige o máximo. Nosso armazenamento nativo inteligente otimiza cada pixel
                                para garantir reprodução instantânea e segurança total.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-green-500/10 rounded-lg">
                                        <ShieldCheck className="w-6 h-6 text-green-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white">Segurança Blindada</h4>
                                        <p className="text-sm text-gray-500">Criptografia ponta-a-ponta para seus arquivos exclusivos.</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-blue-500/10 rounded-lg">
                                        <Zap className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white">CDN Ultra-Rápida</h4>
                                        <p className="text-sm text-gray-500">Carregamento instantâneo independente da localização.</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-purple-500/10 rounded-lg">
                                        <Cloud className="w-6 h-6 text-purple-500" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white">Otimização Automática</h4>
                                        <p className="text-sm text-gray-500">Compressão inteligente sem perda de qualidade visual.</p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};
